import React from "react";
import {useFormik} from "formik";
import * as yup from "yup";

import {useFeedbackTools} from "../../../components/FeedbackTools";
import PasswordField from "../../../components/PasswordField";

import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import TextField from "@mui/material/TextField/TextField";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import AccommodationUnitAutocomplete from "../../../components/Accommodations/AccommodationUnitAutocomplete";
import useAccommodations from "../../../components/Accommodations/useAccommodations";
import {OptionalContent} from "../../../components/OptionalContent";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';


const validationSchema = yup.object().shape({
    nickname: yup
        .string()
        .required('Campo obbligatorio'),
    username: yup
        .string()
        .required('Campo obbligatorio'),
    password: yup
        .string()
        .required('Campo obbligatorio'),
    wskey: yup
        .string()
        .required('Campo obbligatorio'),
    alloggiatiRefs: yup.array().of(
        yup.object().shape({
            ref: yup.string(),
            accommodationUnitIds: yup.array().of(yup.number()),
            _destroy: yup.boolean().default(false)
        }))
        .test('unique-ref', 'Gli ID Alloggiati devono essere diversi tra loro', (value) => {
            const refs = value.filter(r => !r._destroy).map(r => r.ref);
            return refs.length === new Set(refs).size;
        })
});

const AlloggiatiAccountForm = (props) => {
    const {onSave, account} = props
    const {accommodationUnits} = useAccommodations();
    const {askConfirmation} = useFeedbackTools();

    const [isSaving, setIsSaving] = React.useState(false);
    const {showErrorNotification, showSuccessNotification} = useFeedbackTools();

    const formik = useFormik({
        initialValues: {
            nickname: account?.nickname || "",
            username: account?.username || "",
            password: account?.password || "",
            wskey: account?.wskey || "",
            alloggiatiRefs: account?.alloggiatiRefs.map(ref => ({
                id: ref.id,
                ref: ref.ref,
                accommodationUnitIds: ref.accommodationUnitIds,
                _destroy: false
            })) || [],
        },
        validationSchema: validationSchema,
        validateOnMount: true,
        onSubmit: async (values) => {
            setIsSaving(true);
            try {
                const {alloggiatiRefs, key, ...rest} = values;
                await onSave({
                    ...rest,
                    alloggiatiRefsAttributes: alloggiatiRefs.sort(r => r._destroy ? -1 : 1).map(({
                                                                                                     key,
                                                                                                     ...data
                                                                                                 }) => data)
                });
                showSuccessNotification(`Account ${account?.id ? 'aggiornato' : 'creato'}`)
            } catch (e) {
                showErrorNotification(e);
                setIsSaving(false);
            }
        },
    });

    const handleDestroy = (ref, index) => {
        const innerDestroy = async () => {
            if (!!ref.id) {
                formik.setFieldValue(`alloggiatiRefs[${index}]._destroy`, true);
            } else {
                formik.setFieldValue('alloggiatiRefs', formik.values.alloggiatiRefs.filter((_, i) => i !== index));
            }
        }
        askConfirmation({
            title: "Eliminare Account",
            description: "Confermi di voler eliminare l'associazione?",
            confirmationText: 'Ok',
            cancellationText: 'cancella',
            onConfirm: async () => await innerDestroy(),
        })
    }

    const handleAdd = () => {
        formik.setFieldValue('alloggiatiRefs', [...formik.values.alloggiatiRefs, {
            ref: "",
            accommodationUnitIds: [],
            _destroy: false,
            key: Date.now().toString()
        }])
    }

    const disableSave = Object.keys(formik.errors).some(x => formik.touched[x]);

    return <Stack direction="column" spacing={3} sx={{mt: 1}}>
        <TextField
            required
            name="nickname"
            value={formik.values.nickname}
            onChange={formik.handleChange}
            error={formik.touched.nickname && Boolean(formik.errors.nickname)}
            helperText={formik.touched.nickname && formik.errors.nickname}
            label="Nome account"
            autoComplete="new-password"
            fullWidth
        />
        <TextField
            required
            name="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
            label="Alloggiati username"
            autoComplete="new-password"
            fullWidth
        />
        <PasswordField
            required
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            label="Alloggiati password"
            autoComplete="new-password"
            fullWidth
        />
        <PasswordField
            required
            name="wskey"
            value={formik.values.wskey}
            onChange={formik.handleChange}
            error={formik.touched.wskey && Boolean(formik.errors.wskey)}
            helperText={formik.touched.wskey && formik.errors.wskey}
            label="Alloggiati WSKEY"
            autoComplete="new-password"
            fullWidth
        />

        <Divider/>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Associazioni</Typography>
            <Button color="primary" onClick={handleAdd}>
                + Aggiungi
            </Button>
        </Stack>

        <OptionalContent hasContent={formik.values.alloggiatiRefs.filter(r => !r._destroy).length > 0}
                         noContentMessage="Nessuna associazione esistente"
                         showIcon>
            {
                formik.touched.alloggiatiRefs && formik.errors.alloggiatiRefs && <Alert severity="error">
                    {formik.errors.alloggiatiRefs}
                </Alert>
            }
            {
                formik.values.alloggiatiRefs.map((ref, i) => {
                    if (!ref._destroy) {
                        return <Stack key={ref.id || ref.key} direction="row">
                            <Grid container columnSpacing={1} rowSpacing={1}>
                                <Grid item sm={4} xs={12}>
                                    <TextField size="small"
                                               fullWidth
                                               name={`alloggiatiRefs[${i}].ref`}
                                               label={`ID Alloggiati`}
                                               value={ref.ref}
                                               onChange={formik.handleChange}
                                               error={formik.touched.alloggiatiRefs?.[i]?.ref && Boolean(formik.errors.alloggiatiRefs?.[i]?.ref)}
                                               helperText={formik.touched.alloggiatiRefs?.[i]?.ref && formik.errors.alloggiatiRefs?.[i]?.ref}
                                               InputLabelProps={{shrink: true}}/>
                                </Grid>
                                <Grid item sm={8} xs={12} display="flex" flexWrap="nowrap">
                                    <AccommodationUnitAutocomplete
                                        size="small"
                                        onSelect={(selectedItems) => {
                                            formik.setFieldValue(`alloggiatiRefs[${i}].accommodationUnitIds`, selectedItems.map(a => a.id))
                                        }}
                                        accommodationUnits={accommodationUnits}
                                        selectedAccommodationUnits={accommodationUnits.filter(a => ref.accommodationUnitIds.includes(a.id))}
                                        textFieldProps={{
                                            InputLabelProps: {shrink: true},
                                            label: "Appartamenti associati"
                                        }}
                                        fullWidth/>
                                </Grid>
                            </Grid>
                            <IconButton color="error" onClick={() => handleDestroy(ref, i)}>
                                <DeleteOutlineIcon/>
                            </IconButton>
                        </Stack>
                    }
                })
            }
        </OptionalContent>

        <Box sx={{textAlign: 'right'}}>
            <LoadingButton onClick={() => formik.handleSubmit()}
                           disabled={disableSave}
                           loading={isSaving}
                           variant="contained">
                Salva
            </LoadingButton>
        </Box>
    </Stack>
}

export default AlloggiatiAccountForm;